import { Text } from "@chakra-ui/react"
import { Customer, GetCustomerSitesDocument, CustomerSite } from "@hydrologiq/gql-experience/graphql"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import CustomerSitesPaginated from "./CustomerSitesPaginated"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useState, useEffect } from "react"
import { PaginationMeta, SortConfig } from "@hydrologiq/shared/types/pagination"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"

interface CustomerExpandedSitesProps {
  customer: Customer
}

export function CustomerExpandedSites({ customer }: CustomerExpandedSitesProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<SortConfig | null>(null)
  const [sites, setSites] = useState<CustomerSite[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | undefined>(undefined)

  const { data, loading, error } = useQueryWithEndpoint<
    gqlTypes.GetCustomerSitesQuery,
    gqlTypes.GetCustomerSitesQueryVariables
  >(
    GetCustomerSitesDocument,
    {
      variables: {
        id: customer.id,
        page,
        perPage: pageSize,
        sortBy: sort?.field || undefined,
        sortDirection: sort?.direction || "asc",
      },
    },
    "primary"
  )

  useEffect(() => {
    if (data?.customerSites) {
      if (Array.isArray(data.customerSites)) {
        setSites(data.customerSites)
        setPagination(undefined)
      } else {
        const response = data.customerSites as any
        setSites(response.data || [])
        setPagination(response.meta)
      }
    }
  }, [data])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1)
  }

  const handleSortChange = (newSort: SortConfig | null) => {
    setSort(newSort)
    setPage(1)
  }

  if (!loading && sites.length === 0 && !error) {
    return <Text color="gray.500">No sites for this customer</Text>
  }

  return (
    <CustomerSitesPaginated
      customerId={customer.id}
      sites={sites}
      loading={loading}
      error={error?.message ?? null}
      pagination={pagination}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      sort={sort}
      onSortChange={handleSortChange}
    />
  )
}

export default CustomerExpandedSites
